import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarRange } from "lucide-react";
import { useSite } from "@/i18n/SiteContext";
import { euro } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";

export const pkgTr = (p, lang) => {
  const tr = p?.translations || {};
  return { title: tr[lang]?.title || tr.en?.title || p?.slug || "", short: tr[lang]?.short || tr.en?.short || "" };
};

export const tourTitle = (x, lang) => x?.titles?.[lang] || x?.titles?.en || x?.slug;

export const PackageCard = ({ pkg, index = 0 }) => {
  const { t, lang } = useSite();
  const c = pkgTr(pkg, lang);
  return (
    <Reveal delay={(index % 3) * 0.08} y={24} className="h-full">
      <Link to={`/packages/${pkg.slug}`} className="group flex flex-col h-full bg-white rounded-3xl overflow-hidden border border-sand/70 shadow-soft hover:shadow-soft-lg" style={{ transition: "box-shadow .3s ease" }} data-testid={`package-card-${pkg.slug}`}>
        <div className="img-zoom relative aspect-[16/10] bg-sand">
          <img src={pkg.image} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
          <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-ivory text-ocean text-[11px] font-medium tracking-wider uppercase px-3 py-1.5 rounded-full shadow-soft">
            <CalendarRange size={13} /> {t("pkg.days", { n: pkg.days })}
          </span>
          <span className="absolute top-4 right-4 bg-ocean text-ivory text-[11px] font-semibold tracking-wider px-3 py-1.5 rounded-full" data-testid={`package-card-discount-${pkg.slug}`}>
            {t("pkg.off", { pct: pkg.discount_pct })}
          </span>
        </div>
        <div className="flex-1 flex flex-col p-7">
          <h3 className="font-serif text-2xl text-ocean leading-snug group-hover:text-sea" style={{ transition: "color .3s ease" }}>{c.title}</h3>
          <p className="mt-2 text-sm text-ocean/65 leading-relaxed line-clamp-2">{c.short}</p>
          <ol className="mt-5 space-y-2">
            {pkg.tours.map((x, i) => (
              <li key={x.slug} className="flex items-start gap-3 text-sm text-ocean/80">
                <span className="w-6 h-6 shrink-0 rounded-full bg-sage-mist text-sage-dark text-[11px] font-semibold flex items-center justify-center">{i + 1}</span>
                <span className="leading-6 line-clamp-1">{tourTitle(x, lang)}</span>
              </li>
            ))}
          </ol>
          <div className="mt-auto pt-6 flex items-end justify-between border-t border-sand/60 mt-6">
            <div>
              <p className="text-xs text-ocean/50 line-through" data-testid={`package-card-full-${pkg.slug}`}>{t("pkg.instead", { price: euro(pkg.full_price) })}</p>
              <p className="text-sm text-ocean/60">
                <span className="font-serif text-3xl text-ocean" data-testid={`package-card-price-${pkg.slug}`}>{euro(pkg.price)}</span> {t("card.pp")}
              </p>
            </div>
            <span className="flex items-center gap-2 text-sm font-medium text-sea">
              {t("pkg.view")} <ArrowRight size={14} className="group-hover:translate-x-1" style={{ transition: "transform .3s ease" }} />
            </span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
};
